import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { X, Check, Palette } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

interface ContentSession {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  color: string;
  contents: any[];
  order: number;
}

interface SessionManagerProps {
  session?: ContentSession | null;
  onClose: () => void;
  onSessionUpdated: (session: ContentSession) => void;
}

const SessionManager: React.FC<SessionManagerProps> = ({ session, onClose, onSessionUpdated }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [error, setError] = useState<string | null>(null);
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    color: '#0FFF55'
  });

  // Cores predefinidas para sessões
  const predefinedColors = [
    '#0FFF55', '#3B82F6', '#EF4444', '#F59E0B', '#8B5CF6', 
    '#EC4899', '#10B981', '#F97316', '#06B6D4', '#84CC16'
  ];

  useEffect(() => {
    if (session) {
      // Editando sessão existente
      setFormData({
        title: session.title,
        subtitle: session.subtitle,
        description: session.description,
        color: session.color
      });
    } else {
      // Criando nova sessão
      setFormData({
        title: '',
        subtitle: '',
        description: '',
        color: '#0FFF55'
      });
    }
  }, [session]);

  const handleSubmit = async () => {
    if (!user || !formData.title.trim() || !formData.subtitle.trim()) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const sessionData = {
        id: session?.id || `session-${Date.now()}`,
        title: formData.title.trim(),
        subtitle: formData.subtitle.trim(),
        description: formData.description.trim(),
        color: formData.color,
        contents: session?.contents || [],
        order: session?.order || 1
      };

      onSessionUpdated(sessionData);
      onClose();
    } catch (err) {
      setError('Erro inesperado');
      console.error('Erro inesperado:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#121316] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#1f2329]">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">
              {session ? 'Editar Sessão' : 'Nova Sessão'}
            </h2>
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          <Card className="mb-6 bg-[#1a1d23] border-[#1f2329]">
            <CardHeader>
              <CardTitle className="text-white">
                Informações da Sessão
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title" className="text-white">Título Principal *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: ¡Bienvenido Dropshipper!"
                    className="bg-[#0f1114] border-[#1f2329] text-white"
                  />
                </div>
                
                <div>
                  <Label htmlFor="subtitle" className="text-white">Subtítulo *</Label>
                  <Input
                    id="subtitle"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    placeholder="Ex: INÍCIO RÁPIDO"
                    className="bg-[#0f1114] border-[#1f2329] text-white"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description" className="text-white">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição da sessão..."
                  rows={3}
                  className="bg-[#0f1114] border-[#1f2329] text-white"
                />
              </div>

              <div>
                <Label className="text-white">Cor da Sessão</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {predefinedColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        formData.color === color 
                          ? 'border-white scale-110' 
                          : 'border-gray-600 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData({ ...formData, color })}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSubmit}
                  className="flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  {session ? 'Atualizar' : 'Criar'} Sessão
                </Button>
                <Button
                  variant="outline"
                  onClick={onClose}
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SessionManager; 